import { ref } from 'vue';
import type { AnalyticsSummary } from 'src/modules/Analytics/types';
import { useStrapiGet } from 'src/shared/composables/useStrapiApi';

/**
 * Fetches a single student's SCHOOL-ASSIGNED learning summary for a teacher.
 * Backed by GET /school-classes/:classDocumentId/students/:studentDocumentId/stats-summary,
 * which returns the same shape as the Analytics summary ({ data: AnalyticsSummary }).
 */
export function useSchoolStudentStats() {
  const summary = ref<AnalyticsSummary | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  async function fetchStudentSummary(
    classDocumentId: string,
    studentDocumentId: string,
    semesterDocumentId?: string,
  ) {
    loading.value = true;
    error.value = null;
    summary.value = null;

    const {
      data,
      execute,
      error: fetchError,
    } = useStrapiGet(
      `school-classes/${classDocumentId}/students/${studentDocumentId}/stats-summary`,
      semesterDocumentId ? { semesterDocumentId } : {},
    );

    await execute().catch((e: unknown) => {
      error.value =
        (e as Error).message || 'Failed to fetch student statistics';
    });

    if (fetchError.value || !data.value) {
      error.value =
        (fetchError.value as string) || 'Failed to fetch student statistics';
      loading.value = false;
      return;
    }

    // Controller wraps the summary in { data: summary }.
    summary.value = (data.value?.data ?? data.value) as AnalyticsSummary;
    loading.value = false;
  }

  return {
    summary,
    loading,
    error,
    fetchStudentSummary,
  };
}
